import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import FloatCard from '../components/Card/FloatCard'
import TextInput from '../components/Input/TextInput'
import Select from '../components/Input/Select'
import KorwilSelect from '../components/KorwilSelect'
import PrimaryButton from '../components/Button/PrimaryButton'
import FileInput from '../components/Input/FileInput'
import ConfirmModal from '../components/Modal/ConfirmModal'
import ImageInput from '../components/Input/ImageInput'
import {
  getCities,
  getDistricts,
  getProvinces,
  getSubdistricts,
} from '../services/location.service'
import {
  getKorwilByLocation,
  getShirtSizeOptions,
} from '../services/dashboard.service'

const NIK_LENGTH = 16

// Sengaja tidak pakai `maxLength`: browser memotong teks yang ditempel sebelum
// spasi/tanda hubungnya dibuang, jadi NIK yang disalin dengan pemisah malah
// kehilangan digit di belakang.
const keepNikDigits = (e) => {
  e.target.value = e.target.value.replace(/\D/g, '').slice(0, NIK_LENGTH)
}

const MAX_LISTED_FIELDS = 3

// Nama field persis seperti yang terlihat di form (tanpa bintang wajib isi),
// supaya gampang dicocokkan. Input file tidak punya label yang tersambung,
// jadi namanya diambil dari `aria-label`.
const fieldLabelOf = (el) =>
  (el.labels?.[0]?.textContent || el.getAttribute('aria-label') || el.name)
    .replace(/\*$/, '')
    .trim()

// Field yang sudah diisi tapi formatnya salah memakai `title`-nya sebagai
// penjelasan (mis. "NIK harus 16 digit angka").
const describeInvalidField = (el) =>
  !el.validity.valueMissing && el.title ? el.title : fieldLabelOf(el)

const ManualRegistration = ({ onSubmit, title, children, isLoading }) => {
  const [provinceList, setProvinceList] = useState([])
  const [cityList, setCityList] = useState([])
  const [districtList, setDistrictList] = useState([])
  const [subdistrictList, setSubdistrictList] = useState([])
  const [shirtSizes, setShirtSizes] = useState([])
  const [provinceId, setProvinceId] = useState(null)
  const [cityId, setCityId] = useState(null)
  const [districtId, setDistrictId] = useState(null)
  const [subdistrictId, setSubdistrictId] = useState(null)
  const [modal, setModal] = useState(false)
  const submitRef = useRef()
  const formRef = useRef()

  useEffect(() => {
    fetchProvince()
    fetchShirtSizes()
  }, [])

  useEffect(() => {
    fetchCity(provinceId)
  }, [provinceId])

  useEffect(() => {
    fetchDistrict(cityId)
  }, [cityId])

  useEffect(() => {
    fetchSubdistrict(districtId)
  }, [districtId])

  const fetchProvince = async () => {
    try {
      const fetchData = await getProvinces()
      setProvinceList(fetchData.map((d) => ({ label: d.name, value: d.id })))
    } catch (error) {
      console.error(error)
    }
  }

  const fetchCity = async (province_id) => {
    try {
      const fetchData = await getCities({ province_id })
      setCityList(fetchData.map((d) => ({ label: d.name, value: d.id })))
    } catch (error) {
      console.error(error)
    }
  }

  const fetchDistrict = async (city_id) => {
    try {
      const fetchData = await getDistricts({ city_id })
      setDistrictList(fetchData.map((d) => ({ label: d.name, value: d.id })))
    } catch (error) {
      console.error(error)
    }
  }

  const fetchSubdistrict = async (district_id) => {
    try {
      const fetchData = await getSubdistricts({ district_id })
      setSubdistrictList(fetchData.map((d) => ({ label: d.name, value: d.id })))
    } catch (error) {
      console.error(error)
    }
  }

  const fetchShirtSizes = async () => {
    try {
      const fetchData = await getShirtSizeOptions()
      setShirtSizes(fetchData.map((d) => ({ label: d, value: d })))
    } catch (error) {
      console.error(error)
    }
  }

  // Input foto profil dan KTP/KK disembunyikan, jadi browser tidak bisa
  // menampilkan pesan validasinya sendiri (cuma peringatan "not focusable" di
  // console). Karena itu field yang belum benar dicek dulu di sini, sebelum
  // modal konfirmasi dibuka.
  const openConfirm = () => {
    const invalidFields = [...formRef.current.elements].filter(
      (el) => el.willValidate && !el.checkValidity(),
    )
    if (invalidFields.length === 0) {
      setModal(true)
      return
    }

    const listed = invalidFields
      .slice(0, MAX_LISTED_FIELDS)
      .map(describeInvalidField)
      .join(', ')
    const rest = invalidFields.length - MAX_LISTED_FIELDS
    toast.error(
      `Lengkapi dulu: ${listed}${rest > 0 ? ` dan ${rest} lainnya` : ''}`,
    )

    const first = invalidFields[0]
    const isHidden = first.offsetParent === null
    const scrollTarget = isHidden ? first.parentElement : first
    scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (!isHidden) {
      first.focus({ preventScroll: true })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validation korwil by lingkup
    const data = new FormData(e.target)
    const province = data.get('province_id')
    const city = data.get('city_id')
    const district = data.get('district_id')
    const subdistrict = data.get('subdistrict_id')
    const validKorwils = await getKorwilByLocation({
      subdistrict_code: subdistrict,
      district_code: district,
      city_code: city,
      province_code: province,
    })

    if (validKorwils.length === 0) {
      alert('Tidak ada korwil ditemukan pada lingkup tersebut')
      return
    }

    const korwil = data.get('korwil')
    if (!validKorwils.find((k) => k.id === parseInt(korwil))) {
      alert('Korwil yanng anda pilih bukan pada lingkup yang benar')
      return
    }

    onSubmit(e)
  }

  return (
    <form
      ref={formRef}
      onInvalid={() => setModal(false)}
      onSubmit={handleSubmit}
      className="container mx-auto"
      encType="multipart/form-data"
    >
      <h1 className="text-2xl font-semibold mb-4 mt-8">{title}</h1>
      {children}
      <FloatCard>
        <div className="p-4 mt-4">
          <h2 className="text-lg font-semibold">Detail Profil</h2>
          <div className="flex flex-wrap gap-8 mt-2">
            <div>
              <ImageInput
                name="profileImage"
                label="FOTO PROFIL"
                required={true}
              />
            </div>
            <div className="flex-1">
              <TextInput
                label="NAMA LENGKAP"
                placeholder="Masukkan nama lengkap"
                labelBottomLeft="Nama lengkap sesuai KTP atau KK"
                type="text"
                name="fullname"
                required={true}
              />
              <TextInput
                label="TANGGAL LAHIR (MIN. UMUR 13 TAHUN)"
                placeholder=""
                type="date"
                name="birthDate"
                required={true}
              />
              <TextInput
                label="NOMOR INDUK KEPENDUDUKAN"
                placeholder="mis: 0000000000000000"
                labelBottomLeft="Dapat melihat NIK pada KTP ataupun KK (jika belum memiliki KTP)"
                type="text"
                name="nik"
                inputMode="numeric"
                pattern={`[0-9]{${NIK_LENGTH}}`}
                title={`NIK harus ${NIK_LENGTH} digit angka`}
                onInput={keepNikDigits}
                required={true}
              />
              <Select
                label="UKURAN KAOS"
                labelBottomLeft="Kaos akan didistribusikan bersamaan dengan KTA fisik"
                placeholder="--Pilih ukuran kaos--"
                options={shirtSizes}
                name="shirtSize"
                required={true}
              />
            </div>
          </div>
        </div>
      </FloatCard>
      <FloatCard>
        <div className="p-4 mt-4">
          <h2 className="text-lg font-semibold">Domisili</h2>
          <p className="text-[#77787A] text-sm">
            Pastikan pengisian data harus sesuai dengan yang tertera pada KTP/KK
            legal Anda.
          </p>
          <TextInput
            label="ALAMAT LENGKAP"
            placeholder="Masukkan alamat lengkap"
            labelBottomLeft="Tuliskan alamat lengkap sesuai dengan KTP/KK"
            type="text"
            name="address"
            required={true}
          />
          <div className="flex flex-wrap gap-4">
            <Select
              label="PROVINSI"
              placeholder="--Pilih provinsi--"
              options={provinceList}
              required={true}
              value={provinceId}
              onChange={setProvinceId}
            />
            <input
              type="hidden"
              name="province"
              value={provinceList.find((p) => p.value === provinceId)?.label}
            />
            <input
              type="hidden"
              name="province_id"
              value={provinceList.find((p) => p.value === provinceId)?.id}
            />
            <Select
              label="KOTAMADYA/KABUPATEN"
              placeholder="--Pilih kotamadya/kabupaten--"
              options={cityList}
              required={true}
              value={cityId}
              onChange={setCityId}
            />
            <input
              type="hidden"
              name="city"
              value={cityList.find((p) => p.value === cityId)?.label}
            />
            <input
              type="hidden"
              name="city_id"
              value={cityList.find((p) => p.value === cityId)?.id}
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <Select
              label="KECAMATAN"
              placeholder="--Pilih kecamatan--"
              options={districtList}
              required={true}
              value={districtId}
              onChange={setDistrictId}
            />
            <input
              type="hidden"
              name="district"
              value={districtList.find((p) => p.value === districtId)?.label}
            />
            <input
              type="hidden"
              name="district_id"
              value={districtList.find((p) => p.value === districtId)?.id}
            />
            <Select
              label="KELURAHAN/DESA"
              placeholder="--Pilih kelurahan/desa--"
              options={subdistrictList}
              required={true}
              value={subdistrictId}
              onChange={setSubdistrictId}
            />
            <input
              type="hidden"
              name="subdistrict"
              value={
                subdistrictList.find((p) => p.value === subdistrictId)?.label
              }
            />
            <input
              type="hidden"
              name="subdistrict_id"
              value={subdistrictList.find((p) => p.value === subdistrictId)?.id}
            />
          </div>
          <KorwilSelect
            label="KORWIL"
            labelBottomLeft="Anda hanya dapat memilih korwil berdasarkan lingkup daerah sesuai alamat legal Anda"
            name="korwil"
            required={true}
          />
        </div>
      </FloatCard>
      <FloatCard>
        <div className="p-4 mt-4">
          <h2 className="text-lg font-semibold">Email & Nomor Ponsel</h2>
          <TextInput
            label="EMAIL"
            placeholder="email@email.com"
            type="text"
            name="email"
            required={true}
          />
          <TextInput
            label="NOMOR PONSEL"
            placeholder="Masukkan nomor telepon"
            type="text"
            name="phoneNumber"
            required={true}
          />
        </div>
      </FloatCard>
      <FloatCard>
        <div className="p-4 mt-4">
          <h2 className="text-lg font-semibold">Dokumen Pendukung</h2>
          <p className="text-[#77787A] text-sm">
            Upload dokumen pendukung berupa{' '}
            <b>Kartu Tanda Kependudukan (KTP) atau Kartu Keluarga (KK)</b> untuk
            registrasi KTA Jakmania.
          </p>
          <FileInput
            name="ktpImage"
            label="UPLOAD KTP/KK"
            labelBottomLeft="Upload maks. 5MB dengan format PDF, JPG, atau PNG"
            required={true}
          />
        </div>
      </FloatCard>
      <div className="flex justify-end my-4 mr-2 sm:mr-0">
        <PrimaryButton type="button" onClick={openConfirm}>
          Simpan
        </PrimaryButton>
      </div>
      <button type="submit" className="hidden" ref={submitRef} />
      <ConfirmModal
        open={modal}
        onClose={() => setModal(false)}
        onConfirm={() => submitRef.current.click()}
        isLoading={isLoading}
      />
    </form>
  )
}

export default ManualRegistration
